import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import { es, enUS } from "date-fns/locale";
import "react-day-picker/dist/style.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const toLocalDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

const dpClassNames = {
  months: "flex", month: "space-y-3 w-full",
  caption: "flex justify-center relative items-center mb-2",
  caption_label: "font-display text-lg text-[#1A1918]",
  nav_button: "h-7 w-7 opacity-60 hover:opacity-100 transition-opacity",
  nav_button_previous: "absolute left-1", nav_button_next: "absolute right-1",
  table: "w-full border-collapse", head_row: "flex justify-between",
  head_cell: "text-[#5C5A56] w-9 font-normal text-xs uppercase tracking-wide",
  row: "flex w-full justify-between mt-1", cell: "relative p-0 text-center",
  day: "h-9 w-9 p-0 font-light text-[#1A1918] rounded-sm hover:bg-[#8C9B90]/20 transition-colors",
  day_disabled: "text-[#1A1918]/25 line-through hover:bg-transparent cursor-not-allowed",
  day_today: "font-medium text-[#B86B52]",
  day_selected: "bg-[#B86B52] text-white rounded-sm hover:bg-[#B86B52]",
  day_range_middle: "!bg-[#B86B52]/30 !text-[#1A1918] !rounded-none",
  day_range_start: "!bg-[#B86B52] !text-white !rounded-sm", day_range_end: "!bg-[#B86B52] !text-white !rounded-sm",
};

export default function BookingWidget({ property }) {
  const { t, lang } = useLang();
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookedISO, setBookedISO] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const clearErr = (k) => setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  const [hoverDate, setHoverDate] = useState(null);
  const pk = property?.key || "condo-507";

  useEffect(() => {
    api.get(`/availability?property=${pk}`).then((r) => setBookedISO(r.data.blocked || [])).catch(() => {});
  }, [pk]);

  const bookedDates = useMemo(() => bookedISO.map(toLocalDate), [bookedISO]);
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const checkIn = range.from ? iso(range.from) : "";
  const checkOut = range.to ? iso(range.to) : "";

  useEffect(() => {
    if (checkIn && checkOut && checkOut > checkIn) {
      api.post("/quote", { check_in: checkIn, check_out: checkOut, guests, guest_name: "x", guest_email: "x@x.com", origin_url: "x", property: pk })
        .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    } else setQuote(null);
  }, [checkIn, checkOut, guests, pk]);

  const onSelect = (r) => {
    clearErr("dates");
    if (r?.from && r?.to) {
      // reject if any night within range is booked
      const bs = new Set(bookedISO);
      for (let d = new Date(r.from); d < r.to; d.setDate(d.getDate() + 1)) {
        if (bs.has(iso(d))) { toast.error(t("Those dates include unavailable nights. Please pick an open range.")); setRange({ from: undefined, to: undefined }); return; }
      }
    }
    setRange(r || { from: undefined, to: undefined });
  };

  const reserve = async () => {
    const errs = {};
    if (!checkIn || !checkOut) errs.dates = t("Select your check-in and check-out dates");
    if (!name.trim()) errs.name = t("Full name is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = t("Enter a valid email address");
    if (phone.trim().replace(/\D/g, "").length < 7) errs.phone = t("Enter a valid phone number");
    if (!agreed) errs.agree = t("You must agree to the policies & house rules");
    if (Object.keys(errs).length) { setErrors(errs); toast.error(t("Please complete the highlighted fields.")); return; }
    setErrors({});
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        check_in: checkIn, check_out: checkOut, guests,
        guest_name: name, guest_email: email, guest_phone: phone, message, origin_url: window.location.origin,
        property: pk, lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls = "w-full min-w-0 bg-white border border-[#1A1918]/15 rounded-sm px-4 py-3 text-[#1A1918] font-light focus:outline-none focus:border-[#B86B52] transition-colors";

  // live hover preview while dragging out a range (from selected, to not yet chosen)
  const previewDays = [];
  let previewNights = 0;
  if (range.from && !range.to && hoverDate && hoverDate > range.from) {
    const bs = new Set(bookedISO);
    let valid = true;
    for (let d = new Date(range.from.getTime() + 86400000); d <= hoverDate; d.setDate(d.getDate() + 1)) {
      if (bs.has(iso(d))) { valid = false; break; }
      previewDays.push(new Date(d));
    }
    if (!valid) previewDays.length = 0;
    else previewNights = previewDays.length;
  }
  const selectedNights = (checkIn && checkOut) ? Math.round((range.to - range.from) / 86400000) : 0;
  const nightsShown = selectedNights || previewNights;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="booking-widget"
      className="bg-[#F6F4F0] border border-[#1A1918]/10 rounded-sm p-8 lg:p-10 shadow-[0_30px_60px_-25px_rgba(26,25,24,0.15)]">
      <div className="flex items-baseline justify-between mb-6">
        <span className="font-display text-2xl text-[#1A1918]">{t("Reserve your dates")}</span>
        <span className="overline text-[#B86B52]">{t("Direct Booking")}</span>
      </div>

      {property && (property.weekly_discount_pct > 0 || property.monthly_discount_pct > 0 || property.last_minute_discount_pct > 0) && (
        <div data-testid="discount-banner" className="flex flex-wrap gap-2 mb-6">
          {property.weekly_discount_pct > 0 && <span className="text-xs px-3 py-1.5 bg-[#8C9B90]/25 text-[#2A4759] rounded-sm">{t("Save {p}% weekly (7+ nights)").replace("{p}", Math.round(property.weekly_discount_pct * 100))}</span>}
          {property.monthly_discount_pct > 0 && <span className="text-xs px-3 py-1.5 bg-[#8C9B90]/25 text-[#2A4759] rounded-sm">{t("Save {p}% monthly (28+ nights)").replace("{p}", Math.round(property.monthly_discount_pct * 100))}</span>}
          {property.last_minute_discount_pct > 0 && <span className="text-xs px-3 py-1.5 bg-[#B86B52]/15 text-[#B86B52] rounded-sm">{t("Last-minute: save {p}% within {d} days").replace("{p}", Math.round(property.last_minute_discount_pct * 100)).replace("{d}", property.last_minute_window_days)}</span>}
        </div>
      )}

      <div className={`bg-white border rounded-sm p-4 mb-4 ${errors.dates ? "border-red-500" : "border-[#1A1918]/10"}`} data-testid="range-calendar">
        <div className="flex items-center justify-between mb-3 text-sm">
          <div data-testid="selected-checkin"><span className="overline text-[#5C5A56] block text-[10px]">{t("Check In")}</span><span className="text-[#1A1918]">{checkIn || t("Select")}</span></div>
          <span className="text-[#B86B52]">→</span>
          <div className="text-right" data-testid="selected-checkout"><span className="overline text-[#5C5A56] block text-[10px]">{t("Check Out")}</span><span className="text-[#1A1918]">{checkOut || t("Select")}</span></div>
        </div>
        <div onMouseLeave={() => setHoverDate(null)}>
          <DayPicker
            mode="range" numberOfMonths={1} fromMonth={today}
            locale={lang === "es" ? es : enUS}
            selected={range} onSelect={onSelect}
            onDayMouseEnter={(day) => setHoverDate(day)}
            disabled={[{ before: today }, ...bookedDates]}
            modifiers={{ booked: bookedDates, preview: previewDays }}
            modifiersClassNames={{ booked: "av-booked", preview: "av-preview" }}
            classNames={dpClassNames}
          />
        </div>
        <div className="mt-2 text-center" data-testid="nights-indicator">
          {nightsShown > 0 ? (
            <span className="inline-block text-xs px-3 py-1.5 bg-[#B86B52]/15 text-[#B86B52] rounded-sm">
              {nightsShown} {t(nightsShown > 1 ? "nights" : "night")} {selectedNights ? t("selected") : t("— release to select checkout")}
            </span>
          ) : (
            <span className="text-xs text-[#5C5A56] font-light">{t("Click your check-in, then drag to your check-out date")}</span>
          )}
        </div>
        <style>{`.av-booked{background:rgba(184,107,82,0.18)!important;color:#B86B52!important;text-decoration:line-through;}.av-preview{background:rgba(184,107,82,0.22)!important;color:#1A1918!important;border-radius:0!important;}`}</style>
        {errors.dates && <p className="mt-2 text-center text-xs text-red-600" data-testid="error-dates">{errors.dates}</p>}
      </div>

      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-4 mb-4">
        <div className="min-w-0">
          <label className="overline text-[#5C5A56] block mb-2">{t("Guests")}</label>
          <select data-testid="guests-input" value={guests} onChange={(e) => setGuests(Number(e.target.value))} className={inputCls}>
            {Array.from({ length: property?.max_guests ?? 4 }, (_, i) => i + 1).map((n) => (<option key={n} value={n}>{n} {n === 1 ? t("guest") : t("guests")}</option>))}
          </select>
        </div>
        <div className="min-w-0">
          <label className="overline text-[#5C5A56] block mb-2">{t("Phone")} <span className="text-red-500">*</span></label>
          <input data-testid="phone-input" value={phone} onChange={(e) => { setPhone(e.target.value); clearErr("phone"); }} className={`${inputCls} ${errors.phone ? "border-red-500" : ""}`} placeholder={t("Phone number")} />
          {errors.phone && <p className="mt-1 text-xs text-red-600" data-testid="error-phone">{errors.phone}</p>}
        </div>
      </div>

      <input data-testid="name-input" value={name} onChange={(e) => { setName(e.target.value); clearErr("name"); }} className={`${inputCls} ${errors.name ? "border-red-500 mb-1" : "mb-4"}`} placeholder={t("Full name") + " *"} />
      {errors.name && <p className="mb-3 text-xs text-red-600" data-testid="error-name">{errors.name}</p>}
      <input data-testid="email-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); clearErr("email"); }} className={`${inputCls} ${errors.email ? "border-red-500 mb-1" : "mb-4"}`} placeholder={t("Email address") + " *"} />
      {errors.email && <p className="mb-3 text-xs text-red-600" data-testid="error-email">{errors.email}</p>}
      <textarea data-testid="message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className={`${inputCls} mb-6 resize-none`} placeholder={t("Anything we should know? (optional)")} />

      {quote && (
        <div data-testid="quote-breakdown" className="border-t border-[#1A1918]/10 pt-5 mb-6 space-y-2 text-[#5C5A56] font-light">
          <div className="flex justify-between">
            <span>{quote.seasonal ? t("Accommodation · {n} nights").replace("{n}", quote.nights) : `$${quote.nightly_rate} × ${quote.nights} ${t("nights")}`}</span>
            <span>${quote.base_subtotal.toFixed(2)}</span>
          </div>
          {quote.seasonal && <div className="text-xs text-[#B86B52]" data-testid="seasonal-note">{t("Seasonal rates apply · avg ${x}/night").replace("{x}", quote.avg_nightly)}</div>}
          {quote.demand_level >= 0.4 && (
            <div className="text-xs text-[#B86B52] flex items-center gap-1.5" data-testid="demand-note">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B86B52] animate-pulse" />{t("High demand for these dates")}
            </div>
          )}
          {quote.discount_amount > 0 && (
            <div className="flex justify-between text-[#B86B52]" data-testid="quote-discount">
              <span>{t(quote.discount_label)} (−{Math.round(quote.discount_pct * 100)}%)</span>
              <span>−${quote.discount_amount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between"><span>{t("Cleaning fee")}</span><span>${quote.cleaning_fee.toFixed(2)}</span></div>
          {quote.service_fee > 0 && <div className="flex justify-between"><span>{t("Service fee")}</span><span>${quote.service_fee.toFixed(2)}</span></div>}
          <div className="flex justify-between text-[#1A1918] text-lg pt-3 border-t border-[#1A1918]/10 mt-2">
            <span className="font-medium">{t("Total due today")}</span><span data-testid="quote-total" className="font-display text-2xl">${quote.total.toFixed(2)}</span>
          </div>
          {quote.damage_deposit > 0 && (
            <p className="text-xs text-[#2A4759] bg-[#8C9B90]/15 rounded-sm px-3 py-2 mt-2" data-testid="deposit-note">
              {t("A ${x} refundable damage deposit is collected at checkout — returned in full to your payment method within 15 days of checkout, provided there is no damage.").replace("{x}", quote.damage_deposit.toFixed(0))}
            </p>
          )}
          {quote.smart_pricing && (
            <p className="text-[11px] text-[#5C5A56]/70 font-light pt-1" data-testid="dynamic-note">{t("Rates adjust to season, weekends and demand.")}</p>
          )}
        </div>
      )}

      {quote && quote.nights < quote.min_nights_required && (
        <div data-testid="minstay-warning" className="mb-6 -mt-1 text-sm text-[#B86B52] bg-[#B86B52]/10 rounded-sm px-4 py-3">
          {t("A minimum stay of {n} nights is required for these dates.").replace("{n}", quote.min_nights_required)}{quote.peak_season ? ` (${quote.peak_season})` : ""}
        </div>
      )}

      <label className={`flex items-start gap-3 cursor-pointer ${errors.agree ? "mb-1" : "mb-5"}`} data-testid="agree-policies">
        <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); clearErr("agree"); }}
          className="mt-1 w-4 h-4 accent-[#B86B52] shrink-0" data-testid="agree-checkbox" />
        <span className="text-xs text-[#5C5A56] font-light leading-relaxed">
          {t("I have read and agree to the")}{" "}
          <span role="button" tabIndex={0}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); document.getElementById("policies")?.scrollIntoView({ behavior: "smooth" }); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); document.getElementById("policies")?.scrollIntoView({ behavior: "smooth" }); } }}
            className="text-[#B86B52] underline underline-offset-2 hover:text-[#a35c45] cursor-pointer">{t("cancellation policy, house rules & damage terms")}</span>.
        </span>
      </label>
      {errors.agree && <p className="mb-4 text-xs text-red-600" data-testid="error-agree">{errors.agree}</p>}

      <button data-testid="reserve-button" onClick={reserve} disabled={loading || (quote && quote.nights < quote.min_nights_required)}
        className="w-full py-4 bg-[#B86B52] text-white tracking-wide rounded-sm hover:bg-[#a35c45] transition-colors disabled:opacity-60">
        {loading ? t("Redirecting to secure checkout…") : t("Reserve & Pay")}
      </button>
      <p className="text-center text-xs text-[#5C5A56] mt-4 font-light">
        {t("{n}-night minimum · Secure payment via Stripe · Booked dates are crossed out").replace("{n}", property?.min_nights ?? "")}
      </p>
    </motion.div>
  );
}
